import { Preferences } from "@capacitor/preferences";
import axios from "axios";

import { setHasAuthToken } from "modules/anonymousBrowsing/anonymousBrowsingState";
import { queryClient } from "pages/Root";

const persistent = {
  async getItem(key: string) {
    const { value } = await Preferences.get({ key });
    if (value) return value;

    const localValue = localStorage.getItem(key);
    if (localValue) await this.setItem(key, localValue);
    return localValue;
  },
  async setItem(key: string, value: string) {
    return Preferences.set({ key, value });
  },
  async removeItem(key: string) {
    localStorage.removeItem(key);
    return Preferences.remove({ key });
  },
};

const transient = {
  async getItem(key: string) {
    return sessionStorage.getItem(key);
  },
  async setItem(key: string, value: string) {
    return sessionStorage.setItem(key, value);
  },
  async removeItem(key: string) {
    return sessionStorage.removeItem(key);
  },
};

export default class AuthService {
  private _keyName = "repeatmd-app-auth-token";

  async isAuthorized() {
    return !!(await this.loadToken());
  }

  async getToken() {
    return (await transient.getItem(this._keyName)) || (await persistent.getItem(this._keyName));
  }

  async loadToken() {
    const token = await this.getToken();

    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      console.warn("No token found in storage for loadToken.");
      delete axios.defaults.headers.common["Authorization"];
    }
    setHasAuthToken(!!token);
    return token;
  }

  async setToken(token: string | null) {
    if (token) {
      await persistent.setItem(this._keyName, token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setHasAuthToken(true);
      queryClient.clear();
    } else {
      console.warn("No token found in storage for setAuthToken.");
      await persistent.removeItem(this._keyName);
      delete axios.defaults.headers.common["Authorization"];
      setHasAuthToken(false);
      queryClient.clear();
    }
  }

  async setImpersonation(token: string | null) {
    if (token) {
      await transient.setItem(this._keyName, token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setHasAuthToken(true);
    } else {
      console.warn("No token found in storage for setImpersonation.");
      await transient.removeItem(this._keyName);
      delete axios.defaults.headers.common["Authorization"];
      setHasAuthToken(false);
      queryClient.clear();
    }
  }

  async clearAll() {
    await persistent.removeItem(this._keyName);
    await transient.removeItem(this._keyName);
    delete axios.defaults.headers.common["Authorization"];
    setHasAuthToken(false);
    queryClient.clear();
  }
}
